// shared/src/resolvers/llm-row-drop.ts
// 2026-08-07 — per-ROW omission hardening for LLM array-of-object outputs
// (docs/agreement-matrix-row-omission-hardening-2026-08-07-brief-revision.md).
//
// Background: the 2026-07-10 batch made array-of-object LLM fields omission-safe
// at the ARRAY level (`.default([])` — a dropped WHOLE key parses to the valid-
// empty value). It did NOT cover a PRESENT array whose individual rows dropped a
// required inner field: DeepSeek Flash did exactly that on m.synthesis
// agreement_matrix (brief 019fdd99, 2026-08-07) and hard-killed the brief. Six
// more brief-critical schemas share the gap (the four skeptics'
// disconfirming_evidence, a-synthesis cross_ticker_paradoxes, m-inductive
// predicted_observables).
//
// Two hardening shapes, chosen by row TYPE:
//   · DISPLAY-comparison rows (agreement_matrix) — a partial row still renders
//     usefully (indicator + the paths that DID resolve), so KEEP the row, default
//     the blank cell, and emit a PARTIAL_* check_log badge (empty-array-guard.ts).
//   · ATOMIC-content rows (this helper) — an evidence row with no `point`/`cite`,
//     a paradox with no `description`, an observable with no `direction` is pure
//     noise. DROP the incomplete row, keep the valid ones, and log the drop.
//
// Why drop (not default) here: it also honours the enum fail-loud doctrine
// ([[feedback_llm_enum_coerce]], llm-enum-coerce.ts:16-19 — "never invent a
// default for a genuinely unrecognized value"). A row whose coerced enum is
// unrecoverable fails its safeParse and is DISCARDED + logged, never silently
// rebucketed into a fabricated value — and never a brief kill.
//
// Surfacing: a tail `console.warn` on drop (mirrors lenientEnum's pattern). NOT a
// check_log badge — post-parse the dropped rows are gone, and for these fields an
// empty array is a LEGITIMATE emit (skeptic D39 "unimpeachable", a-synthesis rule
// 1 "coherent basket", inductive "no usable trajectory"), so "empty" can't be
// badged as degraded. If a sibling actually starts dropping rows in production,
// the tail warn surfaces it and the badge can be upgraded then — no scaffolding
// for a not-yet-observed failure.

import { z } from 'zod';

// console exists in both Node + Workers; guard for exotic runtimes (mirrors
// lenientEnum in llm-enum-coerce.ts).
const _console = (globalThis as { console?: { warn(msg: string): void } }).console;

/**
 * Build an omission-tolerant array of `row`: a present-but-malformed element is
 * DROPPED (not brief-fatal), a dropped WHOLE key defaults to `[]`. z.infer is the
 * same `Row[]` as `z.array(row).default([])`, so downstream types are unchanged.
 *
 * `label` names the field in the drop log (e.g. 'm.contrarian.skeptic.disconfirming_evidence').
 */
export function tolerantRowArray<T extends z.ZodTypeAny>(
  row:   T,
  label: string,
) {
  return z.preprocess((v) => {
    if (!Array.isArray(v)) return v;   // non-array → let z.array reject/za default handle
    const kept = v.filter((r) => row.safeParse(r).success);
    if (kept.length < v.length) {
      _console?.warn(
        `[llm-row-drop] ${label}: dropped ${v.length - kept.length}/${v.length} malformed row(s) (missing/invalid required field) — kept the valid rows, brief continues`,
      );
    }
    return kept;
  }, z.array(row)).default([]);
}
